import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { Plus as Add, DownloadSimple as Download, File as InsertDriveFile } from '@phosphor-icons/react';

import { importsApi } from '../../lib/apiImports';
import { IMPORT_TEMPLATES, downloadImportTemplate } from '../../lib/importTemplates';
import type { DataImport, ImportStatus } from '../../types/imports';
import { RewindImportModal } from '../../components/imports/RewindImportModal';
import { DataImportWizard } from '../../components/imports/DataImportWizard';
import {
  NotificationProvider,
  useNotification,
} from '../../contexts/NotificationContext';
import NotificationContainer from '../../components/NotificationContainer';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';

const STATUS_COLOR: Record<ImportStatus, 'success' | 'primary' | 'warning' | 'danger' | 'neutral'> = {
  pending: 'neutral',
  analyzing: 'neutral',
  ready: 'primary',
  dry_run: 'warning',
  dry_run_complete: 'warning',
  processing: 'warning',
  completed: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  reversed: 'neutral',
};

const ImportDataPageInner: React.FC = () => {
  const { showError } = useNotification();
  const [imports, setImports] = useState<DataImport[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [rewindImport, setRewindImport] = useState<DataImport | null>(null);
  const [resumeId, setResumeId] = useState<number | undefined>(undefined);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const resp = await importsApi.listImports();
      const list = Array.isArray(resp) ? resp : resp.results;
      setImports(list || []);
    } catch (err) {
      setLoadError(true);
      showError(err instanceof Error ? err.message : 'Failed to load imports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

  }, []);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h1">Import data</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Import Contacts, Opportunities, and Jobs from CSV files. Supports JobNimbus exports
            out of the box.
          </Typography>
        </Box>
        <Button
          startDecorator={<Add />}
          onClick={() => {
            setResumeId(undefined);
            setWizardOpen(true);
          }}
        >
          New Import
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography level="title-sm">CSV Templates</Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Start from a template with the exact columns ApexOS expects — filled-in
                templates are auto-detected and pre-mapped on upload.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {IMPORT_TEMPLATES.map((template) => (
                <Button
                  key={template.key}
                  size="sm"
                  variant="outlined"
                  color="primary"
                  startDecorator={<Download />}
                  onClick={() => downloadImportTemplate(template)}
                >
                  {template.label}
                </Button>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          {loadError ? <Alert color="danger" endDecorator={<Button onClick={load}>Retry</Button>}>Could not load your import history.</Alert> : loading ? (
            <Typography level="body-sm">Loading…</Typography>
          ) : imports.length === 0 ? (
            <Stack alignItems="center" sx={{ p: 4 }}>
              <InsertDriveFile size={48} color="var(--joy-palette-neutral-400)" />
              <Typography level="body-md" sx={{ mt: 1 }}>
                No imports yet.
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Click <strong>New Import</strong> to upload a CSV.
              </Typography>
            </Stack>
          ) : (
            <Sheet variant="plain" sx={{ overflow: 'auto' }}>
              <Table sx={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Rows</th>
                    <th>Created / Updated</th>
                    <th>Errors</th>
                    <th>When</th>
                    <th>By</th>
                    <th style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((imp) => {
                    const resumable = !imp.is_terminal || imp.status === 'dry_run_complete';
                    return (
                      <tr key={imp.id}>
                        <td>{imp.source_file_name || imp.original_filename || `Import #${imp.id}`}</td>
                        <td>{imp.import_type}</td>
                        <td>
                          <Chip size="sm" color={STATUS_COLOR[imp.status] || 'neutral'}>
                            {imp.status}
                          </Chip>
                        </td>
                        <td>{imp.total_rows}</td>
                        <td>
                          {imp.create_count} created / {imp.update_count} updated / {imp.skip_count} skipped
                        </td>
                        <td>
                          {imp.error_count > 0 ? (
                            <a href={importsApi.errorsCsvUrl(imp.id)}>{imp.error_count}</a>
                          ) : (
                            '0'
                          )}
                        </td>
                        <td>{new Date(imp.created_at).toLocaleString()}</td>
                        <td>{imp.created_by_name}</td>
                        <td>
                          <Button
                            size="sm"
                            variant={resumable ? 'solid' : 'plain'}
                            onClick={() => {
                              setResumeId(imp.id);
                              setWizardOpen(true);
                            }}
                          >
                            {resumable ? 'Continue' : 'View'}
                          </Button>
                          {['completed', 'failed', 'cancelled'].includes(imp.status) && (
                            <Button size="sm" variant="plain" color="danger" disabled={!imp.tracking_version}
                              title={!imp.tracking_version ? 'This import predates record tracking.' : 'Preview and rewind this import'}
                              onClick={() => setRewindImport(imp)}>Rewind</Button>
                          )}

                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Sheet>
          )}
        </CardContent>
      </Card>

      {rewindImport && <RewindImportModal dataImport={rewindImport} onClose={() => setRewindImport(null)} onReversed={load} />}

      <DataImportWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setResumeId(undefined);
          load();
        }}
        onCompleted={load}
        resumeImportId={resumeId}
      />
    </Box>
  );
};

export const ImportDataPage: React.FC = () => (
  <NotificationProvider>
    <NotificationContainer />
    <ImportDataPageInner />
  </NotificationProvider>
);

export default ImportDataPage;
